import { useEffect, useState } from "react";
import { Sparkles, Save } from "lucide-react";
import { toast } from "sonner";

import { api } from "../../lib/api";
import { Button } from "../ui/button";
import { Slider } from "../ui/slider";

const TONES = [
    { id: "warm", label: "Warm", note: "Encouraging — leads with strengths" },
    { id: "balanced", label: "Balanced", note: "Honest, with a clear next step" },
    { id: "strict", label: "Strict", note: "Direct, no sugar-coating" },
];

const LANGUAGES = [
    { code: "en", label: "English" },
    { code: "es", label: "Spanish" },
    { code: "fr", label: "French" },
    { code: "de", label: "German" },
    { code: "hi", label: "Hindi" },
    { code: "pt", label: "Portuguese" },
    { code: "it", label: "Italian" },
    { code: "ja", label: "Japanese" },
    { code: "zh", label: "Chinese" },
];

const strictnessLabel = (n) => {
    if (n < 25) return "Generous";
    if (n < 55) return "Honest";
    if (n < 80) return "Tough";
    return "Ruthless";
};

export default function CoachPrefsCard() {
    const [prefs, setPrefs] = useState(null);
    const [saving, setSaving] = useState(false);
    const [dirty, setDirty] = useState(false);

    useEffect(() => {
        api.get("/preferences").then(({ data }) => setPrefs(data)).catch(() => toast.error("Couldn't load prefs"));
    }, []);

    const update = (patch) => {
        setPrefs((p) => ({ ...p, ...patch }));
        setDirty(true);
    };

    const save = async () => {
        setSaving(true);
        try {
            const { data } = await api.put("/preferences", prefs);
            setPrefs(data);
            setDirty(false);
            toast.success("Coach preferences saved");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't save");
        } finally {
            setSaving(false);
        }
    };

    if (!prefs) {
        return (
            <div className="rounded-3xl border border-stone-200/80 dark:border-stone-800 bg-white/70 dark:bg-[#242322]/70 p-6">
                <div className="text-stone-500 italic text-sm">Loading…</div>
            </div>
        );
    }

    return (
        <div className="rounded-3xl border border-stone-200/80 dark:border-stone-800 bg-white/70 dark:bg-[#242322]/70 p-6" data-testid="settings-coach-prefs">
            <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="text-[11px] tracking-[0.2em] uppercase font-bold text-stone-400 flex items-center gap-2">
                    <Sparkles size={12} strokeWidth={1.5} /> Coach preferences
                </div>
                {dirty && (
                    <Button onClick={save} disabled={saving} size="sm" className="rounded-full bg-terracotta hover:bg-[#D05A40] text-white" data-testid="prefs-save">
                        <Save size={12} className="mr-1.5" /> {saving ? "Saving…" : "Save"}
                    </Button>
                )}
            </div>

            {/* Tone */}
            <div className="mt-5">
                <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-stone-400 mb-2">Tone</div>
                <div className="flex gap-2 flex-wrap">
                    {TONES.map((t) => (
                        <button
                            key={t.id}
                            onClick={() => update({ coach_tone: t.id })}
                            data-testid={`tone-${t.id}`}
                            className={`px-4 py-2 rounded-2xl text-left transition-colors border
                                ${prefs.coach_tone === t.id
                                    ? "border-terracotta bg-terracotta/10"
                                    : "border-stone-200 dark:border-stone-700 hover:border-stone-400"}`}
                        >
                            <div className="text-sm font-medium">{t.label}</div>
                            <div className="text-[11px] text-stone-500 mt-0.5">{t.note}</div>
                        </button>
                    ))}
                </div>
            </div>

            {/* Strictness */}
            <div className="mt-6">
                <div className="flex items-baseline justify-between">
                    <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-stone-400">Strictness</div>
                    <div className="text-sm font-medium" data-testid="strictness-label">
                        {strictnessLabel(prefs.coach_strictness)} <span className="text-stone-400 font-normal text-xs">({prefs.coach_strictness}/100)</span>
                    </div>
                </div>
                <Slider
                    value={[prefs.coach_strictness]}
                    onValueChange={([v]) => update({ coach_strictness: v })}
                    min={0}
                    max={100}
                    step={5}
                    className="mt-3"
                    data-testid="strictness-slider"
                />
                <div className="text-[11px] text-stone-400 mt-1">Higher = harder to score 90+, average answers anchor lower.</div>
            </div>

            {/* Language */}
            <div className="mt-6">
                <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-stone-400 mb-2">Spoken language</div>
                <select
                    value={prefs.coach_language}
                    onChange={(e) => update({ coach_language: e.target.value })}
                    className="rounded-full px-4 h-10 border border-stone-200 dark:border-stone-700 bg-white dark:bg-[#1f1e1d] text-sm w-full sm:w-auto"
                    data-testid="language-select"
                >
                    {LANGUAGES.map((l) => (
                        <option key={l.code} value={l.code}>{l.label}</option>
                    ))}
                </select>
                <div className="text-[11px] text-stone-400 mt-1">Used for transcription accuracy and the language of your coach's feedback.</div>
            </div>
        </div>
    );
}
